import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getBrokers, createBroker, updateBroker } from "../../services/firestore";
import "./OwnerLayout.css";

const WIZARD_STEPS = ["Basics","Billing","Photos","Guard rules","Review"];

const EMPTY_BROKER = {
  name: "", dispatcherName: "", dispatcherEmail: "",
  // Folder this broker's paperwork is filed under when Download package
  // saves to a granted folder. Separate from `name` because the folders on
  // disk often read shorter ("Malca Amit" lives in a folder called "Malca").
  // Blank falls back to the broker name.
  saveFolderName: "",
  // Whether that folder gets year/month subfolders. Default true so
  // high-volume brokers stay organised; a broker with a handful of jobs a
  // year files flat, which is how those folders already look.
  saveDateFolders: true,
  // Where the broker wants invoices sent (often a different inbox than
  // the dispatcher — e.g. accountspayable@orion vs. dispatch@orion).
  // Used by the Gmail-drafts integration on Approve to pre-fill the
  // To: address. Leave blank to skip auto-draft for this broker.
  invoiceEmail: "",
  // Subject/body templates with simple {placeholders}: {broker},
  // {jobNumber}, {date}, {total}. Fall back to a sane default in
  // gmail.js when these are blank.
  invoiceSubject: "",
  invoiceBody: "",
  hourlyRate: "", mileageRate: "", minHours: "", flatFee: "",
  // OOT follow rule. Different brokers express "follow then break" differently:
  //   DAI  = 10 minutes      → ootFollowMinutes: 10
  //   LALI = 30 minutes      → ootFollowMinutes: 30
  //   EFFY = 30 minutes      → ootFollowMinutes: 30
  //   MWI  = 30 miles        → ootFollowMiles:   30
  // Set whichever field applies; leave the other blank.
  ootFollowMinutes: "",
  ootFollowMiles: "",
  // Whether the guard should text the sales rep by 11am the day before
  // to confirm the detail (Orion FAQ rule). True for DAI/LALI/EFFY/MWI etc;
  // false for Malca and similar brokers where there's no sales rep contact.
  requiresRepConfirm: false,
  photoRequired: { inbound: true, outbound: true, direct: false },
  guardRules: "",
  billingType: "perJob",
};

export default function RateCards() {
  const { userProfile } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    getBrokers(userProfile.companyId).then((data) => {
      setBrokers(data);
      setLoading(false);
    });
  }, [userProfile]);

  function fmt(n) { return n ? `$${Number(n).toFixed(2)}` : "—"; }

  if (selected) {
    return (
      <BrokerDetail
        broker={selected}
        onBack={() => setSelected(null)}
        onSave={async (id, data) => {
          await updateBroker(id, data);
          setBrokers((bs) => bs.map((b) => b.id === id ? { ...b, ...data } : b));
          setSelected((b) => ({ ...b, ...data }));
        }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Rate cards</div>
          <div className="page-subtitle">{brokers.length} broker{brokers.length !== 1 ? "s" : ""}</div>
        </div>
        <button className="btn btn-blue" onClick={() => setShowWizard(true)}>+ New broker</button>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : brokers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💲</div>
            <div>No brokers yet</div>
            <button className="btn btn-blue" onClick={() => setShowWizard(true)}>Set up your first broker</button>
          </div>
        ) : (
          <div className="panel">
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 90px 90px 80px 80px 100px",
              padding: "10px 16px", background: "#fff", borderBottom: "1px solid #E3E8EF",
              fontSize: 10, fontWeight: 700, color: "#8494AD", gap: 8,
              textTransform: "uppercase", letterSpacing: "0.6px",
            }}>
              <span>Broker · Dispatcher</span><span style={{ textAlign: "right" }}>Hourly</span><span style={{ textAlign: "right" }}>Mileage</span>
              <span style={{ textAlign: "right" }}>Min hrs</span><span style={{ textAlign: "right" }}>Flat fee</span><span style={{ textAlign: "right" }}>Billing type</span>
            </div>
            {brokers.map((b) => (
              <div key={b.id} onClick={() => setSelected(b)} style={{
                display: "grid", gridTemplateColumns: "1fr 90px 90px 80px 80px 100px",
                padding: "13px 16px", borderBottom: "1px solid #F0F2F5",
                alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer",
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#F4F8FC"}
                onMouseLeave={(e) => e.currentTarget.style.background = ""}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0B1C2E" }}>{b.name}</div>
                  <div style={{ fontSize: 10, color: "var(--gold)", fontWeight: 600, marginTop: 2 }}>{b.dispatcherName || "—"}</div>
                </div>
                <span className="mono" style={{ textAlign: "right", color: "#22364D" }}>{fmt(b.hourlyRate)}/hr</span>
                <span className="mono" style={{ textAlign: "right", color: "#22364D" }}>{fmt(b.mileageRate)}/mi</span>
                <span className="mono" style={{ textAlign: "right", color: "var(--gold)", fontWeight: 600 }}>{b.minHours ? `${b.minHours}h` : "—"}</span>
                <span className="mono" style={{ textAlign: "right", color: "var(--gold)", fontWeight: 600 }}>{fmt(b.flatFee)}</span>
                <span className={`chip ${b.billingType === "bundled" ? "chip-gray" : "chip-blue"}`} style={{ fontSize: 10, justifySelf: "end" }}>
                  {b.billingType === "monthly" ? "Monthly"
                    : b.billingType === "bundled" ? "Bundled"
                    : "Per job"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showWizard && (
        <BrokerWizard
          companyId={userProfile?.companyId}
          onClose={() => setShowWizard(false)}
          onCreated={(b) => { setBrokers((bs) => [...bs, b]); setShowWizard(false); }}
        />
      )}
    </div>
  );
}

function BrokerDetail({ broker, onBack, onSave }) {
  const [form, setForm] = useState({
    hourlyRate: broker.hourlyRate || "",
    mileageRate: broker.mileageRate || "",
    minHours: broker.minHours || "",
    flatFee: broker.flatFee || "",
    ootFollowMinutes: broker.ootFollowMinutes || "",
    ootFollowMiles: broker.ootFollowMiles || "",
    requiresRepConfirm: !!broker.requiresRepConfirm,
    guardRules: broker.guardRules || "",
    billingType: broker.billingType || "perJob",
    dispatcherName: broker.dispatcherName || "",
    dispatcherEmail: broker.dispatcherEmail || "",
    saveFolderName: broker.saveFolderName || "",
    // Absent on every card written before this existed — treat as on.
    saveDateFolders: broker.saveDateFolders !== false,
    invoiceEmail: broker.invoiceEmail || "",
    invoiceSubject: broker.invoiceSubject || "",
    invoiceBody: broker.invoiceBody || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    await onSave(broker.id, form);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const rateHistory = broker.rateHistory || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-sm" onClick={onBack}>← Back</button>
          <div>
            <div className="page-title">{broker.name}</div>
            <div className="page-subtitle">Rate card</div>
          </div>
        </div>
        <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
          {saved ? "Saved ✓" : saving ? "Saving..." : "Save changes"}
        </button>
      </div>
      <div className="page-body">
        <div className="two-col" style={{ marginBottom: 16 }}>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Billing rates</div></div>
            {[
              { label: "Hourly rate ($/hr)", key: "hourlyRate" },
              { label: "Mileage rate ($/mi)", key: "mileageRate" },
              { label: "Minimum hours", key: "minHours" },
              { label: "Flat fee ($)", key: "flatFee" },
              { label: "OOT follow time (min)", key: "ootFollowMinutes" },
              { label: "OOT follow distance (mi)", key: "ootFollowMiles" },
            ].map(({ label, key }) => (
              <div key={key} className="field-row">
                <div className="field-label">{label}</div>
                <input type="number" value={form[key]} onChange={(e) => set(key, e.target.value)} style={{ width: 100 }} />
              </div>
            ))}
            <div className="field-row">
              <div className="field-label">Billing type</div>
              <select value={form.billingType} onChange={(e) => set("billingType", e.target.value)}>
                <option value="perJob">Per job notification</option>
                <option value="bundled">Bundled by sales rep (e.g. DAI flat-rate)</option>
                <option value="monthly">Monthly roll-up</option>
              </select>
            </div>
            {/* Day-before rep confirmation: surfaces the tap-to-text card on
                the guard's My Jobs screen the day before each detail. Per
                Orion FAQ: text the rep by 11am, no earlier than the day before. */}
            <div className="field-row" style={{ alignItems: "flex-start" }}>
              <div className="field-label">Day-before rep confirm</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", flex: 1 }}>
                <input type="checkbox"
                  checked={!!form.requiresRepConfirm}
                  onChange={(e) => set("requiresRepConfirm", e.target.checked)} />
                <span>
                  Text rep by 11am the day before
                  <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                    Surfaces a "📱 Text {`{rep}`}" reminder card on the guard's My Jobs
                    the day before each detail. Required by DAI/LALI/EFFY/MWI;
                    leave off for Malca etc. where there's no sales rep contact.
                  </div>
                </span>
              </label>
            </div>
          </div>
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Dispatcher & guard rules</div></div>
            <div className="field-row">
              <div className="field-label">Dispatcher name</div>
              <input value={form.dispatcherName} onChange={(e) => set("dispatcherName", e.target.value)} style={{ flex: 1 }} />
            </div>
            <div className="field-row">
              <div className="field-label">Dispatcher email</div>
              <input type="email" value={form.dispatcherEmail} onChange={(e) => set("dispatcherEmail", e.target.value)} style={{ flex: 1 }} />
            </div>
            {/* Where Download package files this broker's paperwork, under
                the folder granted on the billing screen. Its own field
                because the folders on disk read shorter than the broker
                names ("Malca Amit" lives in "Malca"), and deriving it from
                the name would build a second folder beside the real one. */}
            <div className="field-row">
              <div className="field-label">Save folder</div>
              <div style={{ flex: 1 }}>
                <input
                  value={form.saveFolderName}
                  onChange={(e) => set("saveFolderName", e.target.value)}
                  placeholder={form.name || "same as broker name"}
                  style={{ width: "100%" }}
                />
                <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11, color: "#444", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.saveDateFolders !== false}
                    onChange={(e) => set("saveDateFolders", e.target.checked)}
                  />
                  Sort into year / month subfolders
                </label>
                <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                  Folder name on disk, e.g. <code>Malca</code> for Malca Amit — blank uses the
                  broker name. Files land in{" "}
                  <code>
                    &lt;chosen folder&gt;{"\\"}{form.saveFolderName || form.name || "Broker"}
                    {form.saveDateFolders !== false ? "\\2026\\September 2026" : ""}
                  </code>.
                  {form.saveDateFolders === false && " Unticked, everything sits in the one folder — right for a broker with only a few jobs a year."}
                </div>
                </div>
              </div>
            </div>
            <div className="field-row" style={{ alignItems: "flex-start" }}>
              <div className="field-label" style={{ paddingTop: 6 }}>Guard rules</div>
              <textarea value={form.guardRules} onChange={(e) => set("guardRules", e.target.value)} style={{ flex: 1, minHeight: 60 }} placeholder="Any special instructions for guards on this broker's jobs..." />
            </div>
          </div>

          {/* Gmail draft template — used by the auto-draft integration
              on Approve. Leave invoiceEmail blank to opt this broker
              out of auto-drafts (useful for DAI which gets a forwarded
              schedule email instead). Subject/body support a few
              placeholders documented in gmail.js. */}
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Gmail draft template</div>
              <div className="panel-subtitle">Auto-composed in your Gmail Drafts on Approve. Blank email = no auto-draft for this broker.</div>
            </div>
            <div className="field-row">
              <div className="field-label">Invoice email (To)</div>
              <input type="email" value={form.invoiceEmail}
                onChange={(e) => set("invoiceEmail", e.target.value)}
                placeholder="e.g. accountspayable@orionsecurityservices.com"
                style={{ flex: 1 }} />
            </div>
            <div className="field-row">
              <div className="field-label">Subject</div>
              <input value={form.invoiceSubject}
                onChange={(e) => set("invoiceSubject", e.target.value)}
                placeholder="e.g. {broker} invoice — {jobNumber} — {date}"
                style={{ flex: 1 }} />
            </div>
            <div className="field-row" style={{ alignItems: "flex-start" }}>
              <div className="field-label" style={{ paddingTop: 6 }}>Body</div>
              <textarea value={form.invoiceBody}
                onChange={(e) => set("invoiceBody", e.target.value)}
                placeholder={"Hi team,\n\nAttached is the invoice + POD for {jobNumber} ({date}).\n\nTotal: {total}\n\nThanks,\nST Services"}
                style={{ flex: 1, minHeight: 90 }} />
            </div>
            <div style={{ padding: "8px 14px", fontSize: 10, color: "#888", background: "#fafbfc", borderTop: "1px solid #f0f0f0" }}>
              Placeholders: <code>{`{broker}`}</code> <code>{`{jobNumber}`}</code> <code>{`{date}`}</code> <code>{`{total}`}</code> <code>{`{jobType}`}</code>
            </div>
          </div>
        </div>

        {rateHistory.length > 0 && (
          <div className="panel">
            <div className="panel-header"><div className="panel-title">Rate history</div></div>
            {rateHistory.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", borderBottom: "1px solid #f8f8f8", fontSize: 11 }}>
                <span style={{ color: "#888", width: 80 }}>{r.effectiveDate}</span>
                <span>${r.hourlyRate}/hr</span>
                <span style={{ color: "#888" }}>${r.mileageRate}/mi</span>
                <span style={{ color: "#bbb", flex: 1 }}>min {r.minHours}h</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BrokerWizard({ companyId, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_BROKER });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleFinish() {
    if (!form.name.trim()) return setError("Broker name is required.");
    setSaving(true);
    try {
      const ref = await createBroker({ ...form, companyId });
      onCreated({ id: ref.id, ...form });
    } catch {
      setError("Failed to save. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>New broker</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Step {step + 1} of {WIZARD_STEPS.length} — {WIZARD_STEPS[step]}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>

        <div style={{ display: "flex", padding: "8px 18px", gap: 4, borderBottom: "1px solid #f0f0f0" }}>
          {WIZARD_STEPS.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? "#185FA5" : "#e8e8e8" }} />
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {error && <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7, padding: "8px 10px", fontSize: 11, color: "#a32d2d", marginBottom: 12 }}>{error}</div>}

          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Broker name *</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Malca-Amit" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Dispatcher name</label>
                <input value={form.dispatcherName} onChange={(e) => set("dispatcherName", e.target.value)} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Dispatcher email</label>
                <input type="email" value={form.dispatcherEmail} onChange={(e) => set("dispatcherEmail", e.target.value)} />
              </div>
            </div>
          )}

          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { label: "Hourly rate ($/hr)", key: "hourlyRate" },
                { label: "Mileage rate ($/mi)", key: "mileageRate" },
                { label: "Minimum hours", key: "minHours" },
                { label: "Flat fee ($, leave blank if none)", key: "flatFee" },
                { label: "OOT follow time (min) — DAI=10, LALI/EFFY=30", key: "ootFollowMinutes" },
                { label: "OOT follow distance (mi) — MWI=30", key: "ootFollowMiles" },
              ].map(({ label, key }) => (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>{label}</label>
                  <input type="number" value={form[key]} onChange={(e) => set(key, e.target.value)} style={{ width: 140 }} />
                </div>
              ))}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Billing type</label>
                <select value={form.billingType} onChange={(e) => set("billingType", e.target.value)}>
                  <option value="perJob">Per job notification</option>
                  <option value="bundled">Bundled by sales rep (e.g. DAI flat-rate)</option>
                  <option value="monthly">Monthly roll-up report</option>
                </select>
                <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
                  <strong>Bundled</strong>: one submission per sales rep per schedule email. Use for DAI:
                  the schedule lists multiple details, you complete them on different days, and submit
                  one forwarded email at the end. Set Flat fee above to the per-detail amount ($100 for DAI).
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, cursor: "pointer", marginTop: 4 }}>
                <input type="checkbox"
                  checked={!!form.requiresRepConfirm}
                  onChange={(e) => set("requiresRepConfirm", e.target.checked)} />
                <span>
                  <strong>Day-before rep confirm</strong> — text the rep by 11am the day before
                  <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                    Required by DAI/LALI/EFFY/MWI per Orion FAQ. Leave off for Malca and other
                    brokers without a sales rep contact.
                  </div>
                </span>
              </label>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Select which job types require photos from guards:</div>
              {["inbound","outbound","direct"].map((type) => (
                <label key={type} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
                  <input type="checkbox"
                    checked={form.photoRequired?.[type] || false}
                    onChange={(e) => set("photoRequired", { ...form.photoRequired, [type]: e.target.checked })}
                  />
                  {type.charAt(0).toUpperCase() + type.slice(1)} jobs
                </label>
              ))}
            </div>
          )}

          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>Guard rules / special instructions</label>
              <textarea value={form.guardRules} onChange={(e) => set("guardRules", e.target.value)} style={{ minHeight: 120 }} placeholder="Any special handling instructions for guards on this broker's jobs..." />
            </div>
          )}

          {step === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="info-bar">Review the broker setup before saving. You can edit any of this later from the rate card.</div>
              {[
                ["Broker name", form.name],
                ["Dispatcher", form.dispatcherName || "—"],
                ["Hourly rate", form.hourlyRate ? `$${form.hourlyRate}/hr` : "—"],
                ["Mileage rate", form.mileageRate ? `$${form.mileageRate}/mi` : "—"],
                ["Min hours", form.minHours || "—"],
                ["Flat fee", form.flatFee ? `$${form.flatFee}` : "None"],
                ["Billing type",
                  form.billingType === "monthly" ? "Monthly roll-up"
                  : form.billingType === "bundled" ? "Bundled by sales rep"
                  : "Per job notification"],
              ].map(([label, value]) => (
                <div key={label} style={{ display: "flex", gap: 10, fontSize: 12 }}>
                  <span style={{ color: "#888", width: 130 }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between" }}>
          <button className="btn" onClick={() => step > 0 ? setStep(step - 1) : onClose()}>
            {step === 0 ? "Cancel" : "← Back"}
          </button>
          {step < WIZARD_STEPS.length - 1 ? (
            <button className="btn btn-blue" onClick={() => { setError(""); setStep(step + 1); }}>Next →</button>
          ) : (
            <button className="btn btn-green" onClick={handleFinish} disabled={saving}>{saving ? "Saving..." : "Create broker"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
